/**
 * TREATX-BATCH-BOOT-01 — Canonical Auth Boundary authority.
 *
 * ONE single source of truth for the frontend's notion of auth readiness
 * and role eligibility. Every global context provider MUST gate its
 * initial fetch through this hook — never through:
 *   - a private localStorage token check,
 *   - a route-string comparison,
 *   - a provider-local boolean.
 *
 * Semantics
 * ─────────
 * AuthContext exposes `{ user, loading, isAuthenticated }`.
 *   loading === true                            → AUTH_UNKNOWN / RESOLVING
 *   loading === false && !isAuthenticated       → UNAUTHENTICATED
 *   loading === false && isAuthenticated        → AUTHENTICATED
 *
 * `useAuthGate({ roles })` returns:
 *   - authReady:    true iff auth resolution has completed
 *   - authorized:   true iff authenticated AND (no roles filter OR user's
 *                   role is included in the provided roles list)
 *   - user:         the authenticated user or null
 *
 * Do NOT create a second auth-ready authority.
 */

import { useAuth } from '../context/AuthContext';

export function useAuthGate({ roles } = {}) {
  const { user, loading, isAuthenticated } = useAuth() || {};
  const authReady = loading === false;
  if (!authReady || !isAuthenticated || !user) {
    return { authReady, authorized: false, user: null };
  }
  if (Array.isArray(roles) && roles.length > 0) {
    const userRole = (user.role || '').toLowerCase();
    const allowed = roles.some((r) => (r || '').toLowerCase() === userRole);
    return { authReady, authorized: allowed, user };
  }
  return { authReady, authorized: true, user };
}

export default useAuthGate;
